<template>
    <div v-if="isVisible" class="foxy-modal-overlay flex items-center justify-center p-4" @click.self="closeModal" style="z-index: 9999;">
        <div class="foxy-modal !rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div class="p-6 border-b foxy-modal-section">
                <div class="flex items-center justify-between gap-4">
                    <h3 class="text-2xl font-bold text-foxy-text">
                        {{ privateSaveSuccess ? 'Цитату збережено' : (isEditMode ? 'Редагувати цитату' : 'Додати цитату') }}
                    </h3>
                    <button type="button" @click="closeModal" class="text-foxy-text-secondary hover:text-foxy-text rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action/30" aria-label="Закрити">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
            </div>

            <div v-if="privateSaveSuccess" class="p-6 space-y-5">
                <div class="flex items-start gap-3 rounded-2xl border border-foxy-action/25 bg-foxy-action/5 p-4">
                    <i class="fas fa-check-circle mt-0.5 text-foxy-action" aria-hidden="true"></i>
                    <p class="text-foxy-text">Приватну цитату збережено у вашому профілі.</p>
                </div>
                <a v-if="privateQuotesUrl" :href="privateQuotesUrl" class="foxy-btn-outline inline-flex !px-5 !py-2.5">
                    Переглянути цитати
                </a>
            </div>

            <div v-else class="p-6 space-y-6">
                <div>
                    <label class="block text-sm font-bold text-foxy-text mb-2" for="quote-content">
                        Текст цитати
                        <span class="text-xs font-normal text-foxy-text-secondary ml-2">
                            (<span :class="getContentLengthClass()">{{ quoteContentLength }}</span> / 1000 символів)
                        </span>
                    </label>
                    <div class="relative">
                        <i class="fas fa-quote-left pointer-events-none absolute left-4 top-4 text-sm text-foxy-action/35" aria-hidden="true"></i>
                        <textarea id="quote-content" v-model="quoteContent" rows="7" maxlength="1000"
                            class="foxy-textarea !min-h-[10rem] !resize-y !pl-10" placeholder="Введіть текст цитати..."></textarea>
                    </div>
                    <p v-if="validationErrors.content" class="mt-2 text-sm text-foxy-error" role="alert">{{ validationErrors.content }}</p>
                </div>

                <div>
                    <label class="block text-sm font-bold text-foxy-text mb-2" for="quote-page">Сторінка — необов’язково</label>
                    <input id="quote-page" v-model="pageNumber" type="number" min="1" class="foxy-input" placeholder="Наприклад: 42">
                    <p v-if="validationErrors.page_number" class="mt-2 text-sm text-foxy-error" role="alert">{{ validationErrors.page_number }}</p>
                </div>

                <fieldset class="space-y-3">
                    <legend class="text-sm font-bold text-foxy-text">Видимість</legend>
                    <label class="foxy-card !rounded-xl !p-3 flex items-start gap-3 cursor-pointer">
                        <input v-model="isPublic" type="radio" :value="true" class="mt-1">
                        <span class="text-sm text-foxy-text"><strong>Публічна</strong> — бачитимуть інші читачі</span>
                    </label>
                    <label class="foxy-card !rounded-xl !p-3 flex items-start gap-3 cursor-pointer">
                        <input v-model="isPublic" type="radio" :value="false" class="mt-1">
                        <span class="text-sm text-foxy-text"><strong>Приватна</strong> — бачитимете лише ви</span>
                    </label>
                    <p v-if="validationErrors.is_public" class="text-sm text-foxy-error" role="alert">{{ validationErrors.is_public }}</p>
                </fieldset>
            </div>

            <div v-if="!privateSaveSuccess" class="p-6 border-t foxy-modal-section flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button type="button" @click="closeModal" class="foxy-btn-quiet !px-6 !py-3 !text-base !font-medium">Скасувати</button>
                <button type="button" @click="submitQuote" :disabled="isSubmitting || !quoteContent.trim()"
                    class="foxy-btn-primary !px-8 !py-3 !text-base">
                    {{ isSubmitting ? 'Збереження...' : 'Зберегти цитату' }}
                </button>
            </div>
            <div v-else class="p-6 border-t foxy-modal-section flex justify-end">
                <button type="button" @click="closeModal" class="foxy-btn-primary !px-6 !py-3 !text-base">Закрити</button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'AddQuoteModal',
    props: { bookSlug: { type: String, required: false, default: '' } },
    data() {
        return {
            isVisible: false,
            isEditMode: false,
            editQuoteId: null,
            quoteContent: '',
            pageNumber: null,
            isPublic: true,
            isSubmitting: false,
            validationErrors: {},
            privateSaveSuccess: false,
            privateQuotesUrl: '',
            currentBookSlug: this.bookSlug ? String(this.bookSlug).trim().replace(/[^a-zA-Z0-9_-]/g, '') : '',
        };
    },
    computed: {
        quoteContentLength() { return this.quoteContent ? this.quoteContent.length : 0; },
    },
    watch: {
        bookSlug(newValue) { this.currentBookSlug = this.cleanSlug(newValue); },
    },
    mounted() {
        this.moveToBody();
        const handleEscape = (event) => { if (event.key === 'Escape') this.closeModal(); };
        document.addEventListener('keydown', handleEscape);
        this.$once('hook:beforeDestroy', () => {
            document.removeEventListener('keydown', handleEscape);
            this.removeFromBody();
        });
    },
    methods: {
        cleanSlug(value) { return value ? String(value).trim().replace(/[^a-zA-Z0-9_-]/g, '') : ''; },
        show() { this.resetForm(); this.isVisible = true; },
        showWithData(quoteData, bookSlug = null) {
            if (!quoteData || typeof quoteData !== 'object') {
                this.$emit('show-notification', 'Некоректні дані цитати.', 'error');
                return;
            }
            const cleanBookSlug = this.cleanSlug(bookSlug || quoteData.book_slug || this.bookSlug || this.currentBookSlug);
            if (!cleanBookSlug) {
                this.$emit('show-notification', 'Не вдалося визначити книгу.', 'error');
                return;
            }
            this.isVisible = true;
            this.isEditMode = true;
            this.editQuoteId = quoteData.id ? Number(quoteData.id) : null;
            this.quoteContent = String(quoteData.content || '');
            this.pageNumber = quoteData.page_number || null;
            this.isPublic = quoteData.is_draft ? false : quoteData.is_public !== false;
            this.currentBookSlug = cleanBookSlug;
            this.validationErrors = {};
        },
        hide() { this.isVisible = false; this.resetForm(); },
        closeModal() { this.isVisible = false; this.resetForm(); },
        resetForm() {
            this.isEditMode = false;
            this.editQuoteId = null;
            this.quoteContent = '';
            this.pageNumber = null;
            this.isPublic = true;
            this.isSubmitting = false;
            this.validationErrors = {};
            this.privateSaveSuccess = false;
            this.privateQuotesUrl = '';
        },
        async submitQuote() {
            const content = this.quoteContent;
            this.validationErrors = {};
            if (!content.trim()) {
                this.validationErrors = { content: 'Введіть текст цитати.' };
                return;
            }

            this.isSubmitting = true;
            try {
                const bookSlug = this.currentBookSlug || this.cleanSlug(this.bookSlug);
                const url = this.isEditMode ? `/books/${bookSlug}/quotes/${this.editQuoteId}` : `/books/${bookSlug}/quotes`;
                const method = this.isEditMode ? 'put' : 'post';
                const response = await axios[method](url, {
                    content,
                    page_number: this.pageNumber === '' || this.pageNumber === null ? null : this.pageNumber,
                    is_public: this.isPublic,
                    is_draft: false,
                }, { headers: { Accept: 'application/json' } });

                if (this.isEditMode) {
                    this.$emit('quote-updated', response.data.quote);
                } else if (response.data.quote?.is_public) {
                    this.$emit('quote-added', { ...response.data.quote, book_slug: bookSlug });
                }
                if (this.isPublic) {
                    this.$emit('show-notification', 'Цитату збережено.', 'success');
                    this.closeModal();
                } else {
                    const username = response.data.quote?.user?.username;
                    this.privateQuotesUrl = username ? `/account/u/${username}/quotes-facts?type=quotes` : '';
                    this.privateSaveSuccess = true;
                }
            } catch (error) {
                const errors = error.response?.data?.errors || {};
                this.validationErrors = {
                    content: errors.content?.[0] || '',
                    page_number: errors.page_number?.[0] || '',
                    is_public: errors.is_public?.[0] || '',
                };
                if (!Object.values(this.validationErrors).some(Boolean)) {
                    this.$emit('show-notification', error.response?.data?.message || 'Не вдалося зберегти цитату.', 'error');
                }
            } finally {
                this.isSubmitting = false;
            }
        },
        moveToBody() {
            const element = this.$el;
            if (element && element.parentNode !== document.body) document.body.appendChild(element);
        },
        removeFromBody() {
            const element = this.$el;
            if (element && element.parentNode === document.body) document.body.removeChild(element);
        },
        getContentLengthClass() {
            return this.quoteContentLength > 1000 ? 'text-foxy-error font-semibold' : 'text-foxy-text-secondary';
        },
    },
};
</script>

<style>
.fixed.inset-0 {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
}
</style>
